import {LegacyWBIcon} from '@wandb/common/components/elements/LegacyWBIcon';
import {memo, ReactNode} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Button, Form, Popup} from 'semantic-ui-react';

import * as ProjectPickerQuery from '../graphql/projectPickerQuery';
import {useAdminModeActive} from '../util/admin';

interface ProjectPickerProps {
  entityName: string;
  projectName: string | undefined;
  triggerOverride?: ReactNode;
  requireSameEntity?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  setProject(entityName: string, projectName: string): void;
}

const ProjectPickerComp: React.FC<
  ProjectPickerProps & ProjectPickerQuery.QueryResultProps
> = ({
  entityName,
  projectName,
  triggerOverride,
  requireSameEntity,
  onOpen,
  onClose,
  setProject,
  projectPickerQuery,
}) => {
  const admin = useAdminModeActive();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current) {
      return;
    }
    if (open) {
      onOpen?.();
    } else {
      onClose?.();
    }
    // eslint-disable-next-line
  }, [open]);
  useEffect(() => {
    firstRenderRef.current = false;
  }, []);

  const projects = projectPickerQuery.projects ?? [];

  let projectsWithCurrentProject = projects;
  if (requireSameEntity) {
    projectsWithCurrentProject = projectsWithCurrentProject.filter(
      project => project.entityName === entityName
    );
  }
  let currentProjectIndex = projectsWithCurrentProject.findIndex(
    p => p.entityName === entityName && p.name === projectName
  );
  if (currentProjectIndex === -1 && projectName != null) {
    projectsWithCurrentProject = [
      {
        entityName,
        name: projectName,
        id: '',
        createdAt: '',
      },
      ...projectsWithCurrentProject,
    ];
    currentProjectIndex = 0;
  }

  let projectsWithAdminSuggestion = projectsWithCurrentProject;
  const searchQuerySplit = searchQuery.split(' / ');
  if (admin && searchQuerySplit.length === 2) {
    const [searchEntityName, searchProjectName] = searchQuerySplit;
    const adminSuggestionIncluded = projectsWithCurrentProject.some(
      p => p.entityName === searchEntityName && p.name === searchProjectName
    );
    if (!adminSuggestionIncluded) {
      projectsWithAdminSuggestion = [
        {
          entityName: searchEntityName,
          name: searchProjectName,
          id: '',
          createdAt: '',
        },
        ...projectsWithCurrentProject,
      ];
    }
  }

  const options = projectsWithAdminSuggestion.map((p, i) => ({
    text: p.entityName + ' / ' + p.name,
    value: i,
  }));

  useEffect(() => {
    if (
      !projectPickerQuery.loading &&
      currentProjectIndex === -1 &&
      projectsWithAdminSuggestion.length > 0
    ) {
      setProject(
        projectsWithAdminSuggestion[0].entityName,
        projectsWithAdminSuggestion[0].name
      );
    }
  }, [
    projectPickerQuery.loading,
    currentProjectIndex,
    projectsWithAdminSuggestion,
    setProject,
  ]);

  return (
    <Popup
      basic
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      className="wb-table-action-popup"
      on="click"
      position="bottom left"
      popperModifiers={{
        preventOverflow: {
          // prevent popper from erroneously constraining the popup to the
          // table header
          boundariesElement: 'viewport',
        },
      }}
      trigger={
        triggerOverride ? (
          triggerOverride
        ) : (
          <Button size="tiny" className={'wb-icon-button'}>
            <LegacyWBIcon name="folder" />
            {projectName != null ? entityName + ' / ' + projectName : ''}
          </Button>
        )
      }
      content={
        <Form>
          <Form.Dropdown
            selection
            search
            searchQuery={searchQuery}
            onSearchChange={(e, {searchQuery: newSearchQuery}) =>
              setSearchQuery(newSearchQuery)
            }
            value={currentProjectIndex}
            options={options}
            onChange={(e, {value}) => {
              if (typeof value === 'number') {
                const proj = projectsWithAdminSuggestion[value];
                setProject(proj.entityName, proj.name);
                setOpen(false);
              }
            }}
          />
        </Form>
      }
    />
  );
};

export const ProjectPicker = ProjectPickerQuery.withQuery(
  memo(ProjectPickerComp)
);
